// MULTI-SPACE-ACCESS twin: a channel's or page's shares into more spaces (the API's `space_share`,
// domain/shares/store.ts). The chat and pages fakes each keep their own list and call these: the
// set replaces the item's shares (every space the demo caller sees is one it may add to), never
// the home space, a personal or an archived one, and never the same space twice.
import type { OperationOutput } from "@companyos/sdk";

export type FakeShare = OperationOutput<"pages.shares.list">["data"][number];
type Level = FakeShare["level"];
type SpaceLike = { id: string; kind: string; archived_at: string | null };

const LEVELS: readonly string[] = ["owner", "editor", "viewer"];

/** The refusal of a share set (422 text), or null when it is acceptable. */
export function shareProblem(
  input: unknown,
  homeSpaceId: string | null,
  spaces: readonly SpaceLike[],
  /** A doc's levels are editor and viewer only. */
  levels: readonly string[] = LEVELS,
): string | null {
  if (!Array.isArray(input)) return "shares: a list";
  const seen = new Set<string>();
  for (const s of input as { space_id?: unknown; level?: unknown }[]) {
    const space = spaces.find((x) => x.id === s.space_id);
    if (!space) return "shares: no such space";
    if (!levels.includes(String(s.level))) return `shares: level is ${levels.join(", ")}`;
    if (space.id === homeSpaceId) return "shares: it already lives in this space";
    if (space.kind === "personal") return "shares: a personal space is its owner's only";
    if (space.archived_at) return "shares: the space is archived";
    if (seen.has(space.id)) return "shares: a space is named twice";
    seen.add(space.id);
  }
  return null;
}

/** Replaces the item's shares in `list` (in place) and returns the item's shares. */
export function applyShares(
  list: FakeShare[],
  objectType: FakeShare["object_type"],
  objectId: string,
  input: readonly { space_id: string; level: Level }[],
  at: string,
): FakeShare[] {
  const before = list.filter((s) => s.object_type === objectType && s.object_id === objectId);
  for (const s of before) list.splice(list.indexOf(s), 1);
  for (const s of input) {
    const had = before.find((b) => b.space_id === s.space_id);
    list.push({
      object_type: objectType,
      object_id: objectId,
      space_id: s.space_id,
      level: s.level,
      created_at: had?.created_at ?? at,
    });
  }
  return list.filter((s) => s.object_type === objectType && s.object_id === objectId);
}
